import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Collapse, Spinner, Table } from "reactstrap";
import { getAssetPhotosByBuilding, deleteAssetBuilding } from "../../../../services/ValitanApiService";
import { resolveBackendUrl } from "../../../../helpers/serverUrl";
import { resolveBuildingNameDisplay } from "../../../../utils/otherValueDisplay";
import Swal from "sweetalert2";

const formatDisplayValue = (value) => {
    if (value === undefined || value === null || value === "") return null;
    if (typeof value === "string" || typeof value === "number") return value;
    if (typeof value === "object") {
        return value.name || value.code || value.description || value.label || JSON.stringify(value);
    }
    return String(value);
};

const extractPhotoList = (response) => {
    const payload = response?.data?.data ?? response?.data ?? response ?? [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.content)) return payload.content;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
};

const normalizeMediaUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) {
        return url;
    }
    return resolveBackendUrl(url);
};

const BuildingsTab = ({ assets = [], sourceAssets = [], onRefresh }) => {
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [assetPhotos, setAssetPhotos] = useState({});
    const [loadingAllPhotos, setLoadingAllPhotos] = useState(false);

    const reviewRows = useMemo(() => {
        const assetById = new Map(
            sourceAssets
                .filter((asset) => asset?.id)
                .map((asset) => [String(asset.id), asset])
        );

        return assets.map((item, index) => {
            const linkedAssetId = String(
                item?.asset_id ||
                    item?.assetId ||
                    item?.asset?.id ||
                    item?.raw_asset_id ||
                    item?.id ||
                    ""
            );
            const linkedAsset = assetById.get(linkedAssetId) || {};
            const buildingId = String(
                item?.id ||
                    item?.building_id ||
                    item?.buildingId ||
                    linkedAsset?.building_id ||
                    linkedAsset?.buildingId ||
                    ""
            );

            return {
                ...linkedAsset,
                ...item,
                _assetId: linkedAssetId || String(linkedAsset?.id || ""),
                _buildingId: buildingId,
                _rowKey: String(item?.id || `${linkedAssetId || "building"}-${index}`),
                _sketchImageUrl: normalizeMediaUrl(
                    item?.sketch_image_url ||
                    item?.sketchImageUrl ||
                    linkedAsset?.sketch_image_url ||
                    linkedAsset?.sketchImageUrl ||
                    ""
                ),
                _photoUrls: Array.isArray(item?.photo_urls)
                    ? item.photo_urls
                    : Array.isArray(item?.photoUrls)
                        ? item.photoUrls
                        : [],
            };
        });
    }, [assets, sourceAssets]);

    useEffect(() => {
        if (reviewRows.length === 0) {
            setAssetPhotos({});
            return;
        }

        let isMounted = true;

        const fetchBuildingPhotos = async () => {
            setLoadingAllPhotos(true);
            try {
                const uniqueBuildingIds = [...new Set(
                    reviewRows
                        .map((row) => row?._buildingId)
                        .filter(Boolean)
                        .map(String)
                )];

                const photoEntries = await Promise.all(
                    uniqueBuildingIds.map(async (buildingId) => {
                        try {
                            const response = await getAssetPhotosByBuilding(buildingId, 0, 200);
                            return [buildingId, extractPhotoList(response).map((photo) => ({
                                ...photo,
                                photo_url: normalizeMediaUrl(photo?.photo_url || photo?.photoUrl || photo?.url || photo?.path || ""),
                            }))];
                        } catch (error) {
                            console.error(`Error fetching photos for building ${buildingId}:`, error);
                            return [buildingId, []];
                        }
                    })
                );

                if (!isMounted) return;

                const nextPhotos = Object.fromEntries(photoEntries);
                reviewRows.forEach((row) => {
                    if (row._buildingId && (!nextPhotos[row._buildingId] || nextPhotos[row._buildingId].length === 0) && row._photoUrls.length > 0) {
                        nextPhotos[row._buildingId] = row._photoUrls
                            .filter(Boolean)
                            .map((url, idx) => ({
                                id: `${row._buildingId}-fallback-${idx}`,
                                photo_url: normalizeMediaUrl(url),
                                photo_type: "Photo",
                            }));
                    }
                });

                setAssetPhotos(nextPhotos);
            } catch (error) {
                console.error("Error fetching building photos:", error);
                setAssetPhotos({});
            } finally {
                if (isMounted) {
                    setLoadingAllPhotos(false);
                }
            }
        };

        fetchBuildingPhotos();

        return () => {
            isMounted = false;
        };
    }, [reviewRows]);

    const toggleRow = (asset) => {
        const id = asset._rowKey || asset.id;
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
        } else {
            newExpanded.add(id);
        }
        setExpandedRows(newExpanded);
    };

    const handleDelete = async (asset) => {
        const buildingId = asset._buildingId;
        if (!buildingId) return;

        const result = await Swal.fire({
            title: "Delete Building?",
            text: "Are you sure you want to remove this building? All related accommodations, constructions, and sketches will be deleted.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!"
        });

        if (result.isConfirmed) {
            try {
                await deleteAssetBuilding(buildingId);
                Swal.fire("Deleted!", "Building has been removed.", "success");
                if (onRefresh) onRefresh();
            } catch (error) {
                console.error("Error deleting building", error);
                Swal.fire("Error", "Could not delete building. It might have fixtures or other dependencies.", "error");
            }
        }
    };

    // Prefer human-readable condition text; avoid showing UUIDs
    const getConditionText = (a) => {
        const fromName =
            a?.observed_condition_name ||
            a?.observedConditionName ||
            a?.observed_condition?.name ||
            a?.observedCondition?.name ||
            "";

        if (fromName && String(fromName).trim()) return String(fromName).trim();

        const raw = a?.observed_condition || a?.observedCondition || "";
        const rawStr = typeof raw === "string" ? raw.trim() : "";

        // If raw looks like a UUID, don't display it
        const uuidRegex =
            /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        if (rawStr && uuidRegex.test(rawStr)) return "-";

        return rawStr || "-";
    };

    if (reviewRows.length === 0) {
        return <p className="text-center text-muted py-4">No buildings recorded.</p>;
    }

    const refNumbers = [...new Set(reviewRows.map((asset) => asset.reference_number || asset.valuation_ref || "").filter(Boolean))];
    
    // Color palette for alternating groups
    const groupColors = [
        "#e8f0f5", // Light blue-gray - for first reference_number group
        "#ffffff", // White - for second reference_number group
    ];

    return (
        <React.Fragment>
        <Table responsive hover bordered className="table-sm">
            <thead style={{ backgroundColor: "#0147C8", color: "white" }}>
                <tr>
                    <th style={{ width: "70px" }}>#</th>
                    <th>Building Serial No</th>
                    <th>Reference Number</th>
                    <th>Category</th>
                    <th>Name</th>
                    <th>Building Name</th>
                    <th>Description</th>
                    <th>Building No.</th>
                    <th>Plot Size</th>
                    <th>Use</th>
                    <th>Valuer</th>
                    <th>Status</th>
                    <th>Water</th>
                    <th>Electricity</th>
                    <th>Condition</th>
                    <th>Remark</th>
                </tr>
            </thead>
            <tbody>
                {reviewRows.map((a, index) => {
                    const isExpanded = expandedRows.has(a._rowKey || a.id);
                    const conditionText = getConditionText(a);
                    const hasCondition = conditionText && conditionText !== "-" && conditionText.toLowerCase() !== "n/a";

                    // Derive valuer name from related valuation (if present)
                    const valuation = a.asset_valuation || a.assetValuation || a.valuation;
                    const valuer = valuation?.valuer_user || valuation?.valuerUser;
                    const valuerName =
                        a.valuer_name || a.valuerName ||
                        (valuer
                            ? [
                                  valuer.first_name || valuer.firstName || "",
                                  valuer.middle_name || valuer.middleName || "",
                                  valuer.last_name || valuer.lastName || "",
                              ]
                                  .filter(Boolean)
                                  .join(" ")
                            : "-");
                    
                    // Get current reference number
                    const currentRef = a.reference_number || a.valuation_ref || "";
                    // Get color index for this reference number group
                    const refIndex = refNumbers.indexOf(currentRef);
                    const colorIndex = refIndex >= 0 ? (refIndex % groupColors.length) : 1;
                    const rowBgColor = currentRef ? groupColors[colorIndex] : "#ffffff";
                    
                    // Add custom CSS class for persistent background
                    const rowClassName = currentRef ? `ref-group-${colorIndex}` : "";
                    
                    // Style for the row: background color only
                    const rowStyle = {
                        backgroundColor: rowBgColor
                    };
                    
                    return (
                        <React.Fragment key={a._rowKey || a.id}>
                            <tr className={rowClassName} style={rowStyle}>
                                <td className="fw-bold text-muted">
                                    <div className="d-flex align-items-center gap-1">
                                        <span>{index + 1}</span>
                                        <Button
                                            color="link"
                                            size="sm"
                                            onClick={() => toggleRow(a)}
                                            className="p-0"
                                            style={{ color: "#0147C8", minWidth: "20px" }}
                                        >
                                            <i className={`mdi ${isExpanded ? "mdi-chevron-up" : "mdi-chevron-down"}`} />
                                        </Button>
                                        <Button
                                            color="link"
                                            size="sm"
                                            onClick={() => handleDelete(a)}
                                            className="p-0 text-danger ms-1"
                                            style={{ minWidth: "20px" }}
                                            title="Remove Building"
                                        >
                                            <i className="mdi mdi-delete-outline" />
                                        </Button>
                                    </div>
                                </td>
                                <td>{a.building_serial_no || a.buildingSerialNo || "-"}</td>
                                <td>{a.reference_number || a.valuation_ref || "-"}</td>
                                <td>
                                    {formatDisplayValue(a.category_name) ||
                                        formatDisplayValue(a.asset_category) ||
                                        "Buildings"}
                                </td>
                                <td><strong>{resolveBuildingNameDisplay(a, "Not Specified")}</strong></td>
                                <td>{resolveBuildingNameDisplay(a, "Not Specified")}</td>
                                <td>{a.description || "-"}</td>
                                <td>{a.building_number || a.buildingNumber || "-"}</td>
                                <td>
                                    {a.plot_size || a.plot_area
                                        ? `${(a.plot_size || a.plot_area).toLocaleString()}${a.size_unit ? ` ${a.size_unit}` : " m²"}` 
                                        : "-"}
                                </td>
                                <td>{a.building_use?.name || a.building_use || a.land_use || "-"}</td>
                                <td>
                                    {valuerName || "-"}
                                </td>
                                <td>{a.status || a.valuation_status || "-"}</td>
                                <td>
                                    <Badge color={a.has_water ? "success" : "secondary"}>
                                        {a.has_water ? "Yes" : "No"}
                                    </Badge>
                                </td>
                                <td>
                                    <Badge color={a.has_electricity ? "success" : "secondary"}>
                                        {a.has_electricity ? "Yes" : "No"}
                                    </Badge>
                                </td>
                                <td>
                                    {hasCondition ? conditionText : "-"}
                                </td>
                                <td>{a.remark || a.general_remark || "-"}</td>
                            </tr>
                            {isExpanded && (
                                <tr className={rowClassName} style={{ ...rowStyle, backgroundColor: rowBgColor }}>
                                    <td colSpan="16" className="p-0">
                                        <Collapse isOpen={isExpanded}>
                                            <div className="p-3 bg-light">
                                                <div className="row">
                                                    {/* Basic Information */}
                                                    <div className="col-md-6 mb-3">
                                                        <h6 className="text-primary border-bottom pb-2 mb-2">Basic Information</h6>
                                                        <table className="table table-sm table-borderless mb-0">
                                                            <tbody>
                                                                <tr>
                                                                    <td className="fw-bold" style={{ width: "40%" }}>Barcode:</td>
                                                                    <td>{a.barcode || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Quantity:</td>
                                                                    <td>{a.quantity || 1}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Other Name:</td>
                                                                    <td>{a.other_name || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Description:</td>
                                                                    <td>{a.description || "-"}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {/* Location & Identification */}
                                                    <div className="col-md-6 mb-3">
                                                        <h6 className="text-primary border-bottom pb-2 mb-2">Location & Identification</h6>
                                                        <table className="table table-sm table-borderless mb-0">
                                                            <tbody>
                                                                <tr>
                                                                    <td className="fw-bold" style={{ width: "40%" }}>Plot Number:</td>
                                                                    <td>{a.plot_number || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Block Number:</td>
                                                                    <td>{a.block_number || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Certificate of Title:</td>
                                                                    <td>{a.certificate_of_title || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Registration Number:</td>
                                                                    <td>{a.registration_number || "-"}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {/* Building Details */}
                                                    <div className="col-md-6 mb-3">
                                                        <h6 className="text-primary border-bottom pb-2 mb-2">Building Details</h6>
                                                        <table className="table table-sm table-borderless mb-0">
                                                            <tbody>
                                                                <tr>
                                                                    <td className="fw-bold" style={{ width: "40%" }}>Number of Buildings:</td>
                                                                    <td>{a.number_of_buildings || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Floor:</td>
                                                                    <td>{a.floor || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Room Name/Number:</td>
                                                                    <td>{a.room_name_or_number || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Room Others:</td>
                                                                    <td>{a.room_others || "-"}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {/* Institution Details */}
                                                    <div className="col-md-6 mb-3">
                                                        <h6 className="text-primary border-bottom pb-2 mb-2">Institution Details</h6>
                                                        <table className="table table-sm table-borderless mb-0">
                                                            <tbody>
                                                                <tr>
                                                                    <td className="fw-bold" style={{ width: "40%" }}>Institution Name:</td>
                                                                    <td>{a.institution_name || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Institution Category:</td>
                                                                    <td>{a.institution_category || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Institution Category Other:</td>
                                                                    <td>{a.institution_category_other || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Institution Other:</td>
                                                                    <td>{a.institution_other || "-"}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {/* Land Details */}
                                                    <div className="col-md-6 mb-3">
                                                        <h6 className="text-primary border-bottom pb-2 mb-2">Land Details</h6>
                                                        <table className="table table-sm table-borderless mb-0">
                                                            <tbody>
                                                                <tr>
                                                                    <td className="fw-bold" style={{ width: "40%" }}>Tenure Years:</td>
                                                                    <td>{a.tenure_years || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Rate Per Unit:</td>
                                                                    <td>{a.rate_per_unit ? a.rate_per_unit.toLocaleString() : "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Is Land Surveyed:</td>
                                                                    <td>
                                                                        <Badge color={a.is_land_surveyed ? "success" : "secondary"}>
                                                                            {a.is_land_surveyed ? "Yes" : "No"}
                                                                        </Badge>
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {/* Location Coordinates */}
                                                    <div className="col-md-6 mb-3">
                                                        <h6 className="text-primary border-bottom pb-2 mb-2">Location Coordinates</h6>
                                                        <table className="table table-sm table-borderless mb-0">
                                                            <tbody>
                                                                <tr>
                                                                    <td className="fw-bold" style={{ width: "40%" }}>Latitude:</td>
                                                                    <td>{a.latitude || "-"}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">Longitude:</td>
                                                                    <td>{a.longitude || "-"}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {/* Sketch */}
                                                    <div className="col-12 mb-3">
                                                        <h6 className="text-primary border-bottom pb-2 mb-2">Building Sketch</h6>
                                                        {a._sketchImageUrl ? (
                                                            <div className="row g-3">
                                                                <div className="col-md-6 col-lg-4">
                                                                    <a
                                                                        href={a._sketchImageUrl}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                    >
                                                                        <img
                                                                            src={a._sketchImageUrl}
                                                                            alt={`Sketch for ${resolveBuildingNameDisplay(a)}`}
                                                                            className="img-fluid rounded border"
                                                                            style={{ width: "100%", height: "220px", objectFit: "cover" }}
                                                                        />
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <p className="text-muted">No sketch image available for this building.</p>
                                                        )}
                                                    </div>

                                                    {/* Asset Photos */}
                                                    <div className="col-12 mb-3">
                                                        <h6 className="text-primary border-bottom pb-2 mb-2">Building Photos</h6>
                                                        {loadingAllPhotos ? (
                                                            <div className="d-flex align-items-center gap-2">
                                                                <Spinner size="sm" color="primary" />
                                                                <span className="text-muted">Loading photos...</span>
                                                            </div>
                                                        ) : assetPhotos[a._buildingId] && assetPhotos[a._buildingId].length > 0 ? (
                                                            <div>
                                                                <div className="mb-2">
                                                                    <strong>Total Photos: {assetPhotos[a._buildingId].length}</strong>
                                                                </div>
                                                                <div className="row g-3 mt-1">
                                                                    {assetPhotos[a._buildingId].map((photo, idx) => (
                                                                        <div key={photo.id || idx} className="col-md-4">
                                                                            <a
                                                                                href={photo.photo_url || photo.photoUrl}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                            >
                                                                                <img
                                                                                    src={photo.photo_url || photo.photoUrl}
                                                                                    alt={photo.photo_type || photo.photoType || `Building photo ${idx + 1}`}
                                                                                    className="img-fluid rounded border"
                                                                                    style={{ width: "100%", height: "180px", objectFit: "cover" }}
                                                                                />
                                                                            </a>
                                                                            <div className="small text-muted mt-2">
                                                                                {photo.photo_type || photo.photoType || "Photo"} {idx + 1}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <p className="text-muted">No photos available for this building.</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </Collapse>
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    );
                })}
            </tbody>
        </Table>

        </React.Fragment>
    );
};

export default BuildingsTab;
